import _slicedToArray from "/opt/build/repo/app/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
// src/use-counter.ts
import { useCallbackRef } from "@chakra-ui/react-use-callback-ref";
import { clampValue, countDecimalPlaces, toPrecision } from "@chakra-ui/number-utils";
import { useCallback, useState } from "react";

function useCounter() {
  var props = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var onChange = props.onChange,
      precisionProp = props.precision,
      defaultValue = props.defaultValue,
      valueProp = props.value,
      _props$step = props.step,
      stepProp = _props$step === void 0 ? 1 : _props$step,
      _props$min = props.min,
      min = _props$min === void 0 ? Number.MIN_SAFE_INTEGER : _props$min,
      _props$max = props.max,
      max = _props$max === void 0 ? Number.MAX_SAFE_INTEGER : _props$max,
      _props$keepWithinRang = props.keepWithinRange,
      keepWithinRange = _props$keepWithinRang === void 0 ? true : _props$keepWithinRang;
  var onChangeProp = useCallbackRef(onChange);

  var _useState = useState(function () {
    var _cast;

    if (defaultValue == null) return "";
    return (_cast = cast(defaultValue, stepProp, precisionProp)) !== null && _cast !== void 0 ? _cast : "";
  }),
      _useState2 = _slicedToArray(_useState, 2),
      valueState = _useState2[0],
      setValue = _useState2[1];

  var isControlled = typeof valueProp !== "undefined";
  var value = isControlled ? valueProp : valueState;
  var decimalPlaces = getDecimalPlaces(parse(value), stepProp);
  var precision = precisionProp !== null && precisionProp !== void 0 ? precisionProp : decimalPlaces;
  var update = useCallback(function (next) {
    if (next === value) return;

    if (!isControlled) {
      setValue(next.toString());
    }

    onChangeProp == null ? void 0 : onChangeProp(next.toString(), parse(next));
  }, [onChangeProp, isControlled, value]);
  var clamp = useCallback(function (value2) {
    var nextValue = value2;

    if (keepWithinRange) {
      nextValue = clampValue(nextValue, min, max);
    }

    return toPrecision(nextValue, precision);
  }, [precision, keepWithinRange, max, min]);
  var increment = useCallback(function () {
    var step = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : stepProp;
    var next;

    if (value === "") {
      next = parse(step);
    } else {
      next = parse(value) + step;
    }

    next = clamp(next);
    update(next);
  }, [clamp, stepProp, update, value]);
  var decrement = useCallback(function () {
    var step = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : stepProp;
    var next;

    if (value === "") {
      next = parse(-step);
    } else {
      next = parse(value) - step;
    }

    next = clamp(next);
    update(next);
  }, [clamp, stepProp, update, value]);
  var reset = useCallback(function () {
    var next;

    if (defaultValue == null) {
      next = "";
    } else {
      var _cast2;

      next = (_cast2 = cast(defaultValue, stepProp, precisionProp)) !== null && _cast2 !== void 0 ? _cast2 : min;
    }

    update(next);
  }, [defaultValue, precisionProp, stepProp, update, min]);
  var castValue = useCallback(function (value2) {
    var _cast3;

    var nextValue = (_cast3 = cast(value2, stepProp, precision)) !== null && _cast3 !== void 0 ? _cast3 : min;
    update(nextValue);
  }, [precision, stepProp, update, min]);
  var valueAsNumber = parse(value);
  var isOutOfRange = valueAsNumber > max || valueAsNumber < min;
  var isAtMax = valueAsNumber === max;
  var isAtMin = valueAsNumber === min;
  return {
    isOutOfRange: isOutOfRange,
    isAtMax: isAtMax,
    isAtMin: isAtMin,
    precision: precision,
    value: value,
    valueAsNumber: valueAsNumber,
    update: update,
    reset: reset,
    increment: increment,
    decrement: decrement,
    clamp: clamp,
    cast: castValue,
    setValue: setValue
  };
}

function parse(value) {
  return parseFloat(value.toString().replace(/[^\w.-]+/g, ""));
}

function getDecimalPlaces(value, step) {
  return Math.max(countDecimalPlaces(step), countDecimalPlaces(value));
}

function cast(value, step, precision) {
  var parsedValue = parse(value);
  if (Number.isNaN(parsedValue)) return void 0;
  var decimalPlaces = getDecimalPlaces(parsedValue, step);
  return toPrecision(parsedValue, precision !== null && precision !== void 0 ? precision : decimalPlaces);
}

export { useCounter };